'use client'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import EventsService from '../../services/events-service'
import { FormlessJourneyBaseProps } from '../ApplicationForm/FormlessJourney/FormlessJourney'
import { fireReactGAEvent } from '../Questions/utils'
import { submitFromlessApplication } from '../../utils/formless-utils'
import { buildRedirectUrl } from '../../utils/url-utils'

export type InjectedJourneyProps = {
  apiError: boolean
  conflictError: boolean
  response?: Record<string, any>
  redirectUrl?: string
  closeError: () => Promise<void>
}

const withDetectRefresh = <PropsType extends FormlessJourneyBaseProps>(WrappedComponent: React.ComponentType<PropsType & InjectedJourneyProps>) => {
  const ComponentWithDetectRefresh = (props: PropsType) => {
    const { eventsServiceUrl, appFormEventParams, submissionResponse, mappedApplication, isPreapproved } = props
    const eventsService = useMemo(() => (appFormEventParams ? EventsService(eventsServiceUrl, appFormEventParams) : undefined), [eventsServiceUrl, appFormEventParams])

    const [apiError, setApiError] = useState(false)
    const [conflictError , setConflictError ] = useState(false)
    const [response, setResponse] = useState(submissionResponse)
    
    // Recompute it only when response changes
    const redirectUrl = useMemo(
      () => buildRedirectUrl(response, isPreapproved),
      [response]
    )

    useEffect(() => {
      if (!response?.success) {
        const setError = response?.isConflict ? setConflictError : setApiError
        const id = setTimeout(() => setError(true), 3000)
        return () => clearTimeout(id)
      }
    }, [response])


    const closeError = useCallback(async () => {
      if (!mappedApplication || !eventsService) return
      setApiError(false)
      setConflictError(false)
      const resp = await submitFromlessApplication({
        application: mappedApplication,
        eventsService,
        isRetry: true
      })
      fireReactGAEvent({
        category: 'Application Form',
        action: 'Submit',
        label: resp.success ? 'Success' : 'Unsuccessful'
      })
      setResponse(resp)
    }, [eventsService, mappedApplication])

    const addUrlParam = useCallback(
      (nextRedirectUrl: string) => {
        const url = new URL(window.location.href)
        if (url.searchParams.has('submissionRedirect')) return
        url.searchParams.set('submissionRedirect', nextRedirectUrl)
        // Replace URL instead of pushing to prevent adding it to browser history
        window.history.replaceState({}, '', url.href)
      },
      []
    )

    useEffect(() => {
      if (redirectUrl) addUrlParam(redirectUrl)
    }, [response])

    // Detect refresh only on mount
    useEffect(() => {
      const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
      if (nav?.type === 'reload') {
        eventsService?.sendEvent('AppFormRefreshed')
      }
    }, [eventsService])

    // HOC injects these
    const injected: InjectedJourneyProps = {
      apiError,
      conflictError,
      response,
      redirectUrl,
      closeError
    }

    return (
      <WrappedComponent
        {...props}
        {...injected}
      />
    )
  }

  return ComponentWithDetectRefresh
}

export default withDetectRefresh
